import { Suspense, useEffect, useState } from 'react';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';

import { DetailPanel } from '@/components/detail-panel';
import { NewOrder } from '@/components/admin/new-order';
import { OpenRow } from '@/components/open-row';
import { Pager } from '@/components/pager';
import { usePagedList } from '@/lib/use-paged-list';
import { OrderStatusBadge, formatDate } from '@/components/status-timeline';
import { EmptyState, buttonClass, inputClass } from '@/components/ui';
import { cn } from '@/lib/cn';
import { formatCents } from '@/lib/money';
import { ORDER_STATUS_LABEL, type OrderStatus } from '@/lib/types';
import { adminOrderListQuery, adminShellQuery } from '@/queries';
import { OrderDetail } from './order-detail';

/**
 * Every order, fulfilled and open.
 *
 * The pipeline and this are different questions and had been one screen
 * answering neither. A pipeline is work in progress: five columns, a card per
 * order, and it stops being a pipeline the moment it holds ten thousand
 * finished sales. This is the register: what happened to OG16412, and what has
 * this customer bought, which a board cannot answer once the card has scrolled
 * away.
 *
 * 12,656 orders sit behind it, so nothing is read without a page and the
 * counts on the filter buttons come from the database rather than from the
 * fifty rows on screen.
 */

/** The filters, in the order somebody reaches for them. */
const FILTERS: Array<{ key: string; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'refunded', label: 'Refunded' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'pending_payment', label: 'Awaiting payment' },
];

interface OrderRow {
  id: string;
  ref: string;
  created_at: string;
  status: OrderStatus;
  channel: string;
  source: string | null;
  total_cents: number;
  customer_id: string | null;
  approved_at: string | null;
  xero_invoice_number: string | null;
  xero_state: string | null;
  paid_cents: number | null;
  due_cents: number | null;
  customers: { name: string | null; email: string | null } | null;
}

/**
 * Whether this order is one somebody invoices. Spec 004.
 *
 * Not a Shopify order: those were paid by card at the checkout, and raising an
 * invoice would put a second demand for money in front of a customer who has
 * already paid. Not one already invoiced. Not one with nobody to invoice.
 *
 * What is left is the bulk order taken by phone or email, on terms, which is
 * the whole reason the Xero bridge exists.
 */
function canInvoice(o: OrderRow): boolean {
  return o.channel !== 'shopify'
    && !o.xero_invoice_number
    && Boolean(o.customer_id)
    && o.status !== 'cancelled'
    && o.status !== 'refunded';
}

/**
 * An invoiced order that was then refunded or cancelled owes a credit note.
 *
 * The invoice stays exactly as it is. Xero's own rule and ours: a document
 * that was sent to a customer is not edited afterwards, it is reversed by a
 * second document that says so.
 */
function needsCredit(o: OrderRow): boolean {
  return Boolean(o.xero_invoice_number)
    && (o.status === 'refunded' || o.status === 'cancelled');
}

/** What the money column says, in one short phrase. */
function moneyState(o: OrderRow): { label: string; tone: 'ok' | 'warn' | 'muted' } {
  if (!o.xero_invoice_number) {
    return canInvoice(o)
      ? { label: 'Not invoiced', tone: 'warn' }
      : { label: '', tone: 'muted' };
  }
  const due = Number(o.due_cents ?? 0);
  const paid = Number(o.paid_cents ?? 0);
  if (due <= 0 && paid > 0) return { label: `${o.xero_invoice_number} paid`, tone: 'ok' };
  if (paid > 0) return { label: `${o.xero_invoice_number} part paid`, tone: 'warn' };
  return { label: `${o.xero_invoice_number} unpaid`, tone: 'warn' };
}

export default function AdminOrderListPage() {
  /**
   * `?ref=OG-16657`, which is how the dashboard's recent orders link here.
   *
   * That link has always carried a ref and the board it used to point at never
   * read one, so clicking a recent order landed you on the pipeline with no
   * indication of which one you had asked for. The register can answer it: the
   * ref seeds the search box. R-235.
   */
  const search = useSearch({ strict: false }) as
    { ref?: string; open?: string; filter?: string; page?: string; q?: string };
  const navigate = useNavigate();

  /**
   * THE FILTER, THE PAGE AND THE SEARCH START FROM THE URL, and go back into
   * it whenever they change.
   *
   * This is the whole of "the back button takes me where I was". Opening an
   * order unmounts this screen, so anything held only in React state is gone
   * when the reader comes back, and they used to land on the top of page one
   * of everything. In the URL, going back restores all three, and the router's
   * own `scrollRestoration` restores the position on the page.
   *
   * `replace`, not push, so turning a page does not put an entry in the
   * history. Otherwise "back" from an order walks through every page the
   * reader turned to find it.
   */
  const [filter, setFilter] = useState(search.filter ?? 'all');

  const list = usePagedList<OrderRow>(
    ({ page, q }) => adminOrderListQuery({ page, q, status: filter }) as never,
    [filter],
    { page: Number(search.page) || 1, query: search.q ?? '' },
  );
  const { page, setPage, query, setQuery, needle, rows, isFetching } = list;

  useEffect(() => {
    void navigate({
      to: '/admin/orders',
      replace: true,
      search: (old) => ({
        ...old,
        // Only what differs from the default, so a plain register keeps a
        // plain URL and the link somebody copies is not full of noise.
        filter: filter === 'all' ? undefined : filter,
        page: page === 1 ? undefined : String(page),
        q: needle || undefined,
      }),
    });
  }, [filter, page, needle, navigate]);

  /** The order open over the list. One at a time, from the URL, so it is a link. */
  const openRef = search.open ?? null;
  const data = list.data as { counts?: Record<string, number>; open?: number } | undefined;

  /**
   * `?ref=OG-16657` seeds the search box, which is how the dashboard's recent
   * orders link here. The hook owns the box, so the seed is applied once.
   */
  useEffect(() => {
    if (search.ref) setQuery(search.ref);
  }, [search.ref, setQuery]);
  const qc = useQueryClient();
  const [said, setSaid] = useState<string | null>(null);

  /**
   * Approve, which raises the invoice in Xero and asks Xero to email it.
   *
   * One action, not two. The person deciding the order is real is the person
   * deciding the money is owed, and splitting that into "approve here" and
   * "approve the draft in Xero" means one of the two gets forgotten.
   */
  const approve = useMutation({
    mutationFn: async (orderId: string) => {
      const res = await fetch(`/api/orders/${orderId}/approve`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({}),
      });
      const body = (await res.json().catch(() => null)) as
        | { message?: string; error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not approve it.');
      return body?.message ?? 'Approved.';
    },
    onSuccess: (message) => {
      setSaid(message);
      void qc.invalidateQueries({ queryKey: ['admin', 'order-list'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  /**
   * Credit an invoiced order that has been refunded. FR-X13.
   *
   * A refund is a credit note, never a deleted invoice. The money left the
   * business and the accounts have to show it; a deleted invoice shows nothing
   * and leaves the BAS wrong for the quarter.
   */
  const credit = useMutation({
    mutationFn: async (orderId: string) => {
      const res = await fetch(`/api/orders/${orderId}/credit`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      const body = (await res.json().catch(() => null)) as
        | { message?: string; error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not raise the credit note.');
      return body?.message ?? 'Credit note queued.';
    },
    onSuccess: (message) => {
      setSaid(message);
      void qc.invalidateQueries({ queryKey: ['admin', 'order-list'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });
  /**
   * May this person take one? `orders.create`, asked of the permission matrix.
   * Staff hold it as well as managers, because a phone order is taken by
   * whoever answers the phone.
   */
  const { can } = useSuspenseQuery(adminShellQuery).data;
  const mayCreate = (can.orders ?? []).includes('create');

  const counts = data?.counts ?? {};
  // From the pager, which the hook fills from the response.
  const total = list.pager.total;

  /** The number on a filter button, from the database, over every order. */
  const countFor = (key: string) =>
    key === 'all'
      ? Object.values(counts).reduce((n, c) => n + c, 0)
      : key === 'open'
        ? (data?.open ?? 0)
        : (counts[key] ?? 0);

  return (
    <div>
      <div className="mb-[14px] flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Orders</h1>
          <p className="mt-1 text-[12.5px] text-muted">
            Every order, open and finished, including the history imported from
            Shopify. The pipeline holds the ones still being worked on.
          </p>
        </div>
        {/* Until now an order could only arrive from a machine. The business
            takes them on the telephone and through social media. Spec 006. */}
        {mayCreate && <NewOrder />}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search an order reference"
          aria-label="Search orders"
          className={cn(inputClass, 'min-w-[260px] flex-1')}
        />
        <div className="flex flex-wrap items-center gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
              className={cn(
                'rounded-[10px] border px-3 py-2 text-[13px]',
                filter === f.key ? 'border-line-strong font-semibold' : 'border-line text-muted',
              )}
            >
              {f.label}
              {' '}
              <span className="font-[family-name:var(--font-mono)] tabular-nums text-muted">
                {countFor(f.key).toLocaleString('en-AU')}
              </span>
            </button>
          ))}
        </div>
        {isFetching && <span className="text-[13px] text-muted">Loading...</span>}
      </div>

      {said && (
        <p className="mb-4 rounded-[10px] border border-line bg-panel p-3 text-[13px]">
          {said}
        </p>
      )}

      {rows.length === 0 ? (
        <EmptyState
          title={needle ? 'No order with that reference' : 'No orders yet'}
          body={needle
            ? 'The search covers every order in the book, not only the ones on this page.'
            : 'Orders arrive from Shopify through the bridge, and from the shop when it opens.'}
        />
      ) : (
        <div className="overflow-hidden rounded-[6px] border border-line bg-panel shadow-[var(--shadow-1)]">
          {/* `relative`, so the screen-reader text in the status badges (absolute,
              `sr-only`) stays inside this scroller. Without it those spans
              escaped to the page and made the whole page 265 px wider than a
              phone, so it scrolled sideways (tracker #8). */}
          <div className="relative overflow-x-auto">
            <table className="dt w-full min-w-[720px] text-left">
              <thead>
                <tr>
                  <th scope="col">Reference</th>
                  <th scope="col">Date</th>
                  <th scope="col">Customer</th>
                  <th scope="col">Channel</th>
                  <th scope="col" className="num">Total</th>
                  <th scope="col">Status</th>
                  <th scope="col">Invoice</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((o) => (
                  <OpenRow
                    key={o.id}
                    label={`Open order ${o.ref}`}
                    // `?open=`, so the list stays behind the panel and the
                    // reader keeps their filter, their page and their place.
                    onOpen={() => void navigate({
                      to: '/admin/orders',
                      search: (old) => ({ ...old, open: o.ref }),
                    })}
                  >
                    {/* The reference opens the order.
                        It was plain text, so a register of 12,655 sales
                        answered "what happened to OG16412" with the fact that
                        OG16412 exists and nothing else. Reading an order meant
                        finding its card on the board, which only works while it
                        is still in progress. R-245. */}
                    <td className="font-[family-name:var(--font-mono)] text-[12.5px]">
                      {/* A real anchor, so middle click and cmd click still
                          open an order in a new tab. R-270. */}
                      <Link
                        to="/admin/orders"
                        search={(old) => ({ ...old, open: o.ref })}
                        className="underline underline-offset-2 hover:text-brand-ink"
                      >
                        {o.ref}
                      </Link>
                    </td>
                    <td className="text-[13px] text-muted">{formatDate(o.created_at)}</td>
                    <td className="text-[13px]">
                      {o.customers?.name || o.customers?.email || (
                        <span className="text-muted">no customer recorded</span>
                      )}
                    </td>
                    {/*
                      * CHANNEL, and under it the SOURCE when there is one.
                      *
                      * Two facts, one column, because they are read together
                      * and the register has no room for a thirteenth heading.
                      * The channel says which machine wrote the row; the source
                      * says how the customer reached us. Every imported order
                      * has the first and none has the second. Spec 006.
                      */}
                    <td className="text-[13px] text-muted">
                      {o.channel}
                      {o.source && (
                        <span className="block text-[12px] text-muted/80">
                          {o.source.replace('_', ' ')}
                        </span>
                      )}
                    </td>
                    <td className="num font-[family-name:var(--font-mono)] tabular-nums">
                      {formatCents(Number(o.total_cents))}
                    </td>
                    <td>
                      <OrderStatusBadge status={o.status} />
                      <span className="sr-only">{ORDER_STATUS_LABEL[o.status]}</span>
                    </td>
                    <td className="whitespace-nowrap text-[12.5px]">
                      {canInvoice(o) ? (
                        <button
                          type="button"
                          className={buttonClass('secondary', 'sm')}
                          disabled={approve.isPending}
                          onClick={() => approve.mutate(o.id)}
                        >
                          Approve and invoice
                        </button>
                      ) : needsCredit(o) ? (
                        <button
                          type="button"
                          className={buttonClass('secondary', 'sm')}
                          disabled={credit.isPending}
                          onClick={() => credit.mutate(o.id)}
                        >
                          Raise a credit note
                        </button>
                      ) : (
                        <span className={cn(
                          moneyState(o).tone === 'ok' && 'text-[color:var(--color-ok-ink,inherit)]',
                          moneyState(o).tone !== 'ok' && 'text-muted',
                        )}>
                          {moneyState(o).label}
                        </span>
                      )}
                    </td>
                  </OpenRow>
                ))}
              </tbody>
            </table>
          </div>

          <Pager
            {...list.pager}
            total={total}
            noun="order"
            onPage={(n) => { setPage(n); window.scrollTo({ top: 0 }); }}
          />
        </div>
      )}

      {/*
        * The order, over the list. `Suspense`, because the detail reads the
        * order with a suspending query and the panel is already open by then:
        * without a fallback the whole screen would blank while it loads, which
        * is the thing the panel exists to avoid.
        */}
      <DetailPanel
        open={openRef != null}
        onClose={() => void navigate({
          to: '/admin/orders',
          search: (old) => ({ ...old, open: undefined }),
        })}
        label={openRef ? `Order ${openRef}` : ''}
        title={openRef}
        width="wide"
      >
        {openRef && (
          <Suspense fallback={<PanelLoading />}>
            <OrderDetail key={openRef} refValue={openRef} embedded />
          </Suspense>
        )}
      </DetailPanel>
    </div>
  );
}

/**
 * What the panel shows while the record loads.
 *
 * Shaped like the thing that replaces it, rather than a spinner in an empty
 * box. The panel is open by then, so a spinner reads as "this is broken" for
 * the half second before the order arrives.
 */
function PanelLoading() {
  return (
    <div className="space-y-4 p-5" aria-hidden>
      <div className="h-4 w-24 rounded bg-raised" />
      <div className="h-7 w-48 rounded bg-raised" />
      <div className="h-20 rounded-[10px] bg-raised" />
      <div className="h-20 rounded-[10px] bg-raised" />
      <div className="h-32 rounded-[10px] bg-raised" />
    </div>
  );
}
